/* eslint-disable eqeqeq */
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
	Spin,
	Row,
	Col,
	Tag,
	Button,
	Tabs,
	Form,
	Input,
	Select,
	Checkbox,
	Radio,
	Space,
	InputNumber,
} from 'antd';
import { useParams, useHistory, Prompt } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { AppDispatch } from 'redux/store';
import {
	AgeRating,
	MovementIntensity,
	Genre,
	Language,
	PlayStyle,
	ProductPlayStyleItem,
	ProjectProductDetailsDto,
	UpdateProjectStoreDetailsCommand,
} from 'api';

import FormItem from 'components/form/FormItem';
import {
	getGenres,
	getAllGenres,
	getAllAgeRatings,
	getAllMovementIntensities,
} from 'utils/productUtils';
import OmniNotification from 'components/OmniNotification';
import * as yup from 'yup';
import styles from './StoreDetailsPage.module.less';
import {
	getProjectProduct,
	updateProjectStoreDetails,
} from 'redux/reducers/projectsSlice';
import { ControllerRenderProps, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import cx from 'classnames';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import AssetsForm from './AssetsForm';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useQuery } from 'utils/locationUtils';
import { isEmpty } from 'lodash';
import RouteLeavingGuard from 'components/RouteLeavingGuard';
import { confirmResultType } from 'components/RouteLeavingGuard/RouteLeavingGuard';
const { TabPane } = Tabs;
type ParamsProps = {
	projectId: string;
};
const MAX_PLAYERS = 999999999;
const MAX_PRICE = 99999.99;

interface FormValues {
	name: string;
	description: string;
	publisher: string;
	developer: string;
	sameAsDeveloper: boolean;
	genres: Genre[];
	languages: Language[];
	ageRatingId: AgeRating;
	movementIntensityId: MovementIntensity;
	website: string;
	price?: number;
	supportedPlayStyles: boolean;
	onlineCoopPlayStyleMinimum: number;
	onlineCoopPlayStyleMaximum: number;
	onlinePvpPlayStyleMinimum: number;
	onlinePvpPlayStyleMaximum: number;
	onlineSocialPlayStyleMinimum: number;
	onlineSocialPlayStyleMaximum: number;
}

const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		name: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('addProductPage.form.ProductName'),
				}),
			)
			.max(
				50,
				t('validation.maxLength', {
					fieldName: t('addProductPage.form.ProductName'),
					count: 50,
				}),
			),
		website: yup
			.string()
			.nullable()
			.max(
				400,
				t('validation.maxLength', {
					fieldName: t('common.Website'),
					count: 400,
				}),
			),
		description: yup
			.string()
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('addProductPage.form.Description'),
				}),
			)
			.max(
				2000,
				t('validation.maxLength', {
					fieldName: t('addProductPage.form.Description'),
					count: 2000,
				}),
			),
		publisher: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('addProductPage.form.Publisher'),
				}),
			)
			.when('sameAsDeveloper', {
				is: false,
				then: yup
					.string()
					.required(
						t('validation.required', {
							fieldName: t('addProductPage.form.Publisher'),
						}),
					)
					.max(
						120,
						t('validation.maxLength', {
							fieldName: t('addProductPage.form.Publisher'),
							count: 120,
						}),
					),
			})
			.max(
				120,
				t('validation.maxLength', {
					fieldName: t('addProductPage.form.Publisher'),
					count: 120,
				}),
			),
		developer: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('addProductPage.form.Developer'),
				}),
			)
			.max(
				120,
				t('validation.maxLength', {
					fieldName: t('addProductPage.form.Developer'),
					count: 120,
				}),
			),
		genres: yup
			.array()
			.min(
				1,
				t('validation.requiredPlural', {
					fieldName: t('addProductPage.Genre'),
				}),
			)
			.of(yup.number())
			.nullable(),
		price: yup
			.number()
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('addProductPage.ProductPrice'),
				}),
			)
			.min(0, t('addProductPage.ProductPrice.validation'))
			.max(
				MAX_PRICE,
				t('validation.maxNumber', {
					fieldName: t('addProductPage.ProductPrice'),
					count: MAX_PRICE,
				}),
			),
		ageRatingId: yup.number().required(
			t('validation.required', {
				fieldName: t('addProductPage.AgeRating'),
			}),
		),
		movementIntensityId: yup.number().required(
			t('validation.required', {
				fieldName: t('addProductPage.MovementIntensity'),
			}),
		),
		supportedPlayStyles: yup
			.mixed()
			.nullable()
			.test(
				'supportedPlayStyles',
				t('validation.requiredPlural', {
					fieldName: t('addProductPage.form.SupportedPlayStyles'),
				}),
				(value) => !!value,
			),
		onlineCoopPlayStyleMaximum: yup
			.number()
			.nullable()
			.typeError(t('validation.integer'))
			.when('$onlineCoopSelected', (onlineCoopSelected, schema) => {
				if (onlineCoopSelected) {
					return schema
						.test(
							'notZero',
							t('validation.cantBeZero', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
							(value: number) => value !== 0,
						)
						.positive(
							t('validation.positive', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
						);
				}
				return;
			})
			.integer(t('validation.integer'))
			.max(
				MAX_PLAYERS,
				t('validation.maxNumber', {
					fieldName: t('addProductPage.NumberOfPlayers'),
					count: MAX_PLAYERS,
				}),
			),
		onlinePvpPlayStyleMaximum: yup
			.number()
			.nullable()
			.typeError(t('validation.integer'))
			.when('$onlinePvpSelected', (onlinePvpSelected, schema) => {
				if (onlinePvpSelected) {
					return schema
						.test(
							'notZero',
							t('validation.cantBeZero', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
							(value: number) => value !== 0,
						)
						.positive(
							t('validation.positive', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
						);
				}
				return;
			})
			.integer(t('validation.integer'))
			.max(
				MAX_PLAYERS,
				t('validation.maxNumber', {
					fieldName: t('addProductPage.NumberOfPlayers'),
					count: MAX_PLAYERS,
				}),
			),
		onlineSocialPlayStyleMaximum: yup
			.number()
			.nullable()
			.typeError(t('validation.integer'))
			.when('$onlineSocialSelected', (onlineSocialSelected, schema) => {
				if (onlineSocialSelected) {
					return schema
						.test(
							'notZero',
							t('validation.cantBeZero', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
							(value: number) => value !== 0,
						)
						.positive(
							t('validation.positive', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
						);
				}
				return;
			})
			.integer(t('validation.integer'))
			.max(
				MAX_PLAYERS,
				t('validation.maxNumber', {
					fieldName: t('addProductPage.NumberOfPlayers'),
					count: MAX_PLAYERS,
				}),
			),
		onlineCoopPlayStyleMinimum: yup
			.number()
			.nullable()
			.typeError(t('validation.integer'))
			.when('$onlineCoopSelected', (onlineCoopSelected, schema) => {
				if (onlineCoopSelected) {
					return schema
						.test(
							'notZero',
							t('validation.cantBeZero', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
							(value: number) => value !== 0,
						)
						.positive(
							t('validation.positive', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
						);
				}
				return;
			})
			.integer(t('validation.integer'))
			.max(
				MAX_PLAYERS,
				t('validation.maxNumber', {
					fieldName: t('addProductPage.NumberOfPlayers'),
					count: MAX_PLAYERS,
				}),
			),
		onlinePvpPlayStyleMinimum: yup
			.number()
			.nullable()
			.typeError(t('validation.integer'))
			.when('$onlinePvpSelected', (onlinePvpSelected, schema) => {
				if (onlinePvpSelected) {
					return schema
						.test(
							'notZero',
							t('validation.cantBeZero', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
							(value: number) => value !== 0,
						)
						.positive(
							t('validation.positive', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
						);
				}
				return;
			})
			.integer(t('validation.integer'))
			.max(
				MAX_PLAYERS,
				t('validation.maxNumber', {
					fieldName: t('addProductPage.NumberOfPlayers'),
					count: MAX_PLAYERS,
				}),
			),
		onlineSocialPlayStyleMinimum: yup
			.number()
			.nullable()
			.typeError(t('validation.integer'))
			.when('$onlineSocialSelected', (onlineSocialSelected, schema) => {
				if (onlineSocialSelected) {
					return schema
						.test(
							'notZero',
							t('validation.cantBeZero', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
							(value: number) => value !== 0,
						)
						.positive(
							t('validation.positive', {
								fieldName: t('addProductPage.NumberOfPlayers'),
							}),
						);
				}
				return;
			})
			.integer(t('validation.integer'))
			.max(
				MAX_PLAYERS,
				t('validation.maxNumber', {
					fieldName: t('addProductPage.NumberOfPlayers'),
					count: MAX_PLAYERS,
				}),
			),
	});
const defaultValues: FormValues = {
	website: '',
	developer: '',
	publisher: '',
	description: '',
	ageRatingId: AgeRating.Everyone,
	movementIntensityId: MovementIntensity.Low,
	name: '',
	genres: [],
	languages: [0],
	price: undefined,
	sameAsDeveloper: false,
	supportedPlayStyles: false,
	onlineCoopPlayStyleMinimum: 0,
	onlineCoopPlayStyleMaximum: 0,
	onlinePvpPlayStyleMinimum: 0,
	onlinePvpPlayStyleMaximum: 0,
	onlineSocialPlayStyleMinimum: 0,
	onlineSocialPlayStyleMaximum: 0,
};
export default function StoreDetailsPage() {
	const { t } = useTranslation();
	const query = useQuery();
	const history = useHistory();
	const { projectId } = useParams<ParamsProps>();
	const validationSchema: any = getValidationSchema(t);
	const dispatch: AppDispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(true);
	const [confirmResult, setConfirmResult] = useState<confirmResultType>({
		loading: false,
		result: false,
	});
	const manuallySave = useRef(false);
	const [productDetails, setProductDetails] =
		useState<ProjectProductDetailsDto>();
	const allGenres = getAllGenres(t);
	const [onlineCoopSelected, setOnlineCoopSelected] = useState(false);
	const [onlinePvpSelected, setOnlinePvpSelected] = useState(false);
	const [onlineSocialSelected, setOnlineSocialSelected] = useState(false);
	const [singlePlayerSelected, setSinglePlayerSelected] = useState(false);
	const ageRatings = getAllAgeRatings(t);
	const allMovementIntensities = getAllMovementIntensities(t);
	const [assetsFormComplete, setAssetsFormComplete] = useState(false);
	const [
		shouldCallSubmitWhenNavigatingAway,
		setShouldCallSubmitWhenNavigatingAway,
	] = useState(false);
	const tabIndex = query.get('tab') ?? '1';
	const hasPlayStyleSelected = (
		productPlayStyles: ProductPlayStyleItem[] | undefined,
		playStyle: PlayStyle,
	) => {
		return getProductPlayStyleById(productPlayStyles, playStyle) != undefined;
	};
	const getProductPlayStyleById = (
		productPlayStyles: ProductPlayStyleItem[] | undefined,
		playStyle: PlayStyle,
	) => {
		return productPlayStyles?.find(
			(productPlayStyle) => productPlayStyle.playStyleId == playStyle,
		);
	};
	const pageForm = useForm<FormValues>({
		mode: 'all',
		resolver: yupResolver(validationSchema),
		context: {
			onlineCoopSelected,
			onlinePvpSelected,
			onlineSocialSelected,
		},
		defaultValues,
	});
	const {
		control,
		setValue,
		handleSubmit,
		watch,
		trigger,
		reset,
		clearErrors,
		formState,
	} = pageForm;
	const developer = watch('developer');
	const sameAsDeveloper = watch('sameAsDeveloper');
	useEffect(() => {
		const loadProductDetails = async () => {
			setIsLoading(true);
			const result = await dispatch(getProjectProduct(parseInt(projectId)));
			setIsLoading(false);

			if (!getProjectProduct.fulfilled.match(result)) {
				OmniNotification.error({
					message: t('notification.error.title'),
					description: result.error.message || t('api.defaultError'),
				});
				return;
			} else if (result.payload) {
				setProductDetails(result.payload);
				setOnlineCoopSelected(
					hasPlayStyleSelected(result.payload.playStyles, PlayStyle.OnlineCoop),
				);
				setOnlinePvpSelected(
					hasPlayStyleSelected(result.payload.playStyles, PlayStyle.OnlinePvP),
				);
				setOnlineSocialSelected(
					hasPlayStyleSelected(
						result.payload.playStyles,
						PlayStyle.OnlineSocial,
					),
				);
				setSinglePlayerSelected(
					hasPlayStyleSelected(
						result.payload.playStyles,
						PlayStyle.SinglePlayer,
					),
				);

				if (
					result.payload.galleryItems &&
					result.payload.galleryItems.length > 0
				) {
					setAssetsFormComplete(true);
				}

				const values = {
					...result.payload,
					languages: [0],
				};

				reset(values);

				setValue(
					'sameAsDeveloper',
					!!result.payload.developer &&
						result.payload.developer === result.payload.publisher,
				);
				setSupportedPlayStylesValue(!isEmpty(result.payload.playStyles));
				setValue(
					'onlineCoopPlayStyleMinimum',
					getProductPlayStyleById(
						result.payload?.playStyles,
						PlayStyle.OnlineCoop,
					)?.minPlayers || 0,
				);
				setValue(
					'onlineCoopPlayStyleMaximum',
					getProductPlayStyleById(
						result.payload?.playStyles,
						PlayStyle.OnlineCoop,
					)?.maxPlayers || 0,
				);
				setValue(
					'onlinePvpPlayStyleMinimum',
					getProductPlayStyleById(
						result.payload?.playStyles,
						PlayStyle.OnlinePvP,
					)?.minPlayers || 0,
				);
				setValue(
					'onlinePvpPlayStyleMaximum',
					getProductPlayStyleById(
						result.payload?.playStyles,
						PlayStyle.OnlinePvP,
					)?.maxPlayers || 0,
				);
				setValue(
					'onlineSocialPlayStyleMinimum',
					getProductPlayStyleById(
						result.payload?.playStyles,
						PlayStyle.OnlineSocial,
					)?.minPlayers || 0,
				);
				setValue(
					'onlineSocialPlayStyleMaximum',
					getProductPlayStyleById(
						result.payload?.playStyles,
						PlayStyle.OnlineSocial,
					)?.maxPlayers || 0,
				);
			}
		};

		loadProductDetails();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, projectId]);

	useEffect(() => {
		setSupportedPlayStylesValue(
			singlePlayerSelected ||
				onlineCoopSelected ||
				onlinePvpSelected ||
				onlineSocialSelected,
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		singlePlayerSelected,
		onlineCoopSelected,
		onlinePvpSelected,
		onlineSocialSelected,
	]);

	const setSupportedPlayStylesValue = (value: boolean) => {
		setValue('supportedPlayStyles', value);
	};

	const getPlayStyleArray = (values: FormValues) => {
		const playStyles: ProductPlayStyleItem[] = [];
		if (onlineCoopSelected) {
			playStyles.push({
				playStyleId: PlayStyle.OnlineCoop,
				maxPlayers: values.onlineCoopPlayStyleMaximum,
				minPlayers: values.onlineCoopPlayStyleMinimum,
			});
		}
		if (onlinePvpSelected) {
			playStyles.push({
				playStyleId: PlayStyle.OnlinePvP,
				maxPlayers: values.onlinePvpPlayStyleMaximum,
				minPlayers: values.onlinePvpPlayStyleMinimum,
			});
		}
		if (onlineSocialSelected) {
			playStyles.push({
				playStyleId: PlayStyle.OnlineSocial,
				maxPlayers: values.onlineSocialPlayStyleMaximum,
				minPlayers: values.onlineSocialPlayStyleMinimum,
			});
		}
		if (singlePlayerSelected) {
			playStyles.push({
				playStyleId: PlayStyle.SinglePlayer,
				minPlayers: 1,
				maxPlayers: 1,
			});
		}
		return playStyles;
	};

	const onSubmit = async (values: FormValues) => {
		const data: UpdateProjectStoreDetailsCommand = {
			...values,
			supportedPlayStyles: getPlayStyleArray(values),
		};
		if (!manuallySave.current) {
			setConfirmResult({
				loading: true,
				result: false,
			});
		}
		const result = await dispatch(
			updateProjectStoreDetails({ projectId: parseInt(projectId), ...data }),
		);
		if (updateProjectStoreDetails.fulfilled.match(result)) {
			reset({ ...values }, { keepDirty: false });
			OmniNotification.success({
				message: t('addProductPage.ProjectDetails.success.message', {
					type: t('addProductPage.ProjectDetails'),
				}),
				description: t('addProductPage.ProjectDetails.success.description', {
					projectName: values.name,
					type: t('addProductPage.ProjectDetails'),
				}),
			});
			if (!manuallySave.current) {
				setConfirmResult({
					loading: false,
					result: true,
				});
			}
		} else {
			OmniNotification.error({
				message: t('notification.error.title'),
			});
			if (!manuallySave.current) {
				setConfirmResult({
					loading: false,
					result: false,
				});
			}
		}
	};

	const handleFinish = async () => {
		manuallySave.current = true;
		await handleSubmit(onSubmit)();
	};

	const handleTagClick = (
		field: ControllerRenderProps,
		clickedTag: Genre,
		checked: boolean,
	) => {
		const nextSelectedTags = checked
			? [...field.value, clickedTag]
			: field.value.filter((genre: Genre) => genre !== clickedTag);

		field.onChange(nextSelectedTags);
	};

	const handleSameAsDeveloperClick = (
		field: ControllerRenderProps,
		event: CheckboxChangeEvent,
	) => {
		if (event.target.checked) {
			setValue('publisher', developer);
		}
		trigger('publisher');
		field.onChange(event);
	};
	const handleDeveloperChange = (
		field: ControllerRenderProps,
		event: ChangeEvent<HTMLInputElement>,
	) => {
		if (sameAsDeveloper) {
			setValue('publisher', event.target.value);
			trigger('publisher');
		}
		field.onChange(event);
	};

	const updateParams = (activeKey: string) => {
		const params = new URLSearchParams();
		if (activeKey) {
			params.append('tab', activeKey);
		} else {
			params.delete('tab');
		}
		history.push({ search: params.toString() });
	};

	useEffect(() => {
		if (shouldCallSubmitWhenNavigatingAway) {
			manuallySave.current = false;
			handleSubmit(onSubmit)();
		}
	}, [shouldCallSubmitWhenNavigatingAway]);

	if (isLoading || !productDetails) return <Spin data-testid="loader" />;

	return (
		<>
			<RouteLeavingGuard
				when={!isEmpty(formState.dirtyFields)}
				confirmResult={confirmResult!}
				confirmHandler={() => {
					manuallySave.current = false;
					setShouldCallSubmitWhenNavigatingAway(true);
				}}
				isFormValid={formState.isValid}
			/>
			<div className={styles.productDetailsContainer}>
				<div className="page-head">
					<div>
						<h2 className="h2">{t('projectDetailsPage.Header')}</h2>
					</div>
				</div>
				<Tabs
					activeKey={tabIndex}
					className={styles.tabContent}
					onChange={(activeKey) => {
						// setActiveTab(activeKey);
						updateParams(activeKey);
					}}
				>
					<TabPane tab={t('addProductPage.ProjectDetails')} key="1">
						{tabIndex === '1' && (
							<FormProvider {...pageForm}>
								<Form
									data-testid="form"
									layout="vertical"
									onFinish={handleFinish}
									noValidate
								>
									<div className="page-wrapper scroll-box">
										<div>
											<div className={styles.formGroup}>
												<Row gutter={[20, 32]}>
													<Col xs={24} lg={12}>
														<FormItem
															name="name"
															label={t('addProductPage.form.Name')}
															control={control}
															hint={t('addProductPage.form.Name.hint')}
															render={({ field }) => (
																<Input
																	size="large"
																	{...field}
																	placeholder={t(
																		'addProductPage.form.Name.Placeholder',
																	)}
																/>
															)}
															className={styles.nameField}
														/>
													</Col>
													<Col xs={24} lg={12}>
														<FormItem
															name="website"
															label={t('addProductPage.form.Website')}
															control={control}
															hint={t('addProductPage.form.Website.hint')}
															render={({ field }) => (
																<Input
																	size="large"
																	{...field}
																	placeholder="https://www.example.com/"
																/>
															)}
															className={styles.nameField}
														/>
													</Col>
													<Col xs={24} lg={12} data-testid="developerFormItem">
														<FormItem
															name="developer"
															label={t('addProductPage.form.Developer')}
															hint={t('addProductPage.form.Developer.hint')}
															control={control}
															render={({ field }) => (
																<Input
																	size="large"
																	{...field}
																	placeholder={t(
																		'addProductPage.form.Developer.Placeholder',
																	)}
																	onChange={(event) =>
																		handleDeveloperChange(field, event)
																	}
																	data-testid="developerInput"
																/>
															)}
															className={styles.nameField}
														/>
													</Col>
													<Col xs={24} lg={12} data-testid="publisherFormItem">
														<FormItem
															name="publisher"
															label={t('addProductPage.form.Publisher')}
															control={control}
															render={({ field }) => (
																<Input
																	size="large"
																	disabled={sameAsDeveloper}
																	{...field}
																	placeholder={t(
																		'addProductPage.form.Publisher.Placeholder',
																	)}
																	data-testid="publisherInput"
																/>
															)}
															className={cx(styles.publisherField)}
														/>
														<FormItem
															name="sameAsDeveloper"
															control={control}
															className={'hint'}
															render={({ field }) => (
																<div ref={field.ref}>
																	<Checkbox
																		checked={field.value}
																		{...field}
																		onChange={(event) =>
																			handleSameAsDeveloperClick(field, event)
																		}
																		data-testid="sameAsDeveloperCheckbox"
																	>
																		{t('addProductPage.form.SameAsDeveloper')}
																	</Checkbox>
																</div>
															)}
														/>
													</Col>
													<Col xs={24}>
														<FormItem
															name="description"
															label={t('addProductPage.form.Description')}
															control={control}
															className={cx('has-counter', styles.textField)}
															render={({ field }) => (
																<>
																	<Input.TextArea
																		size="large"
																		rows={4}
																		{...field}
																		defaultValue={productDetails?.description}
																		placeholder={t(
																			'addProductPage.form.Description.Placeholder',
																		)}
																	/>
																	<span className="form-counter">
																		{field.value?.length || 0}/2000
																	</span>
																</>
															)}
														/>
													</Col>
													<Col xs={12}>
														{productDetails && (
															<FormItem
																label={t('addProductPage.Genre')}
																name="genres"
																control={control}
																hint={t('addProductPage.form.Genre.hint')}
																render={({ field, fieldState }) => (
																	<>
																		<Select
																			onSelect={() => clearErrors('genres')}
																			mode="multiple"
																			getPopupContainer={(trigger) =>
																				trigger.parentNode
																			}
																			allowClear
																			defaultValue={getGenres(
																				t,
																				productDetails?.genres || [],
																			).map((genre) => {
																				return {
																					label: genre.name,
																					value: genre.value,
																				};
																			})}
																			style={{ width: '50%' }}
																			placeholder={t(
																				'addProductPage.form.Genre.Placeholder',
																			)}
																			ref={field.ref}
																			labelInValue={true}
																			showArrow
																			onChange={(value: any[]) => {
																				setValue(
																					'genres',
																					value.map((v) =>
																						parseInt(v.value),
																					) as any,
																				);
																				clearErrors('genres');
																			}}
																		>
																			{allGenres.map((genre) => (
																				<Select.Option
																					key={genre.value}
																					value={genre.value}
																					checked={
																						field.value?.indexOf(genre.value) >
																						-1
																					}
																					onChange={(checked: any) =>
																						handleTagClick(
																							field,
																							genre.value,
																							checked,
																						)
																					}
																				>
																					{genre.name}
																				</Select.Option>
																			))}
																		</Select>
																	</>
																)}
															/>
														)}
													</Col>
													<Col xs={12}>
														<FormItem
															name="price"
															label={t('addProductPage.ProductPrice')}
															hint={t('addProductPage.ProductPrice.hint')}
															control={control}
															className={styles.numberField}
															render={({ field }) => (
																<InputNumber
																	{...field}
																	addonBefore="$"
																	controls={false}
																	precision={2}
																	size="large"
																/>
															)}
														/>
													</Col>
													<Col xs={12}>
														<FormItem
															label={
																<div>
																	<div>{t('addProductPage.AgeRating')}</div>
																	<div className="label-hint">
																		{t('addProductPage.AgeRating.hint')}
																	</div>
																</div>
															}
															name="ageRatingId"
															control={control}
															render={({ field, fieldState }) => (
																<div ref={field.ref} tabIndex={0}>
																	<Radio.Group {...field}>
																		<Space direction="vertical">
																			{ageRatings.map((rating) => (
																				<Radio
																					key={rating.value}
																					value={rating.value}
																				>
																					{rating.name}
																				</Radio>
																			))}
																		</Space>
																	</Radio.Group>
																</div>
															)}
														/>
													</Col>
													<Col xs={12}>
														<FormItem
															label={
																<div>
																	<div>
																		{t('addProductPage.MovementIntensity')}
																	</div>
																	<div className="label-hint">
																		{t('addProductPage.MovementIntensity.hint')}
																	</div>
																</div>
															}
															name="movementIntensityId"
															control={control}
															render={({ field, fieldState }) => (
																<div ref={field.ref}>
																	<Radio.Group {...field}>
																		<Space direction="vertical">
																			{allMovementIntensities.map((mi) => (
																				<Radio key={mi.value} value={mi.value}>
																					{mi.name}
																				</Radio>
																			))}
																		</Space>
																	</Radio.Group>
																</div>
															)}
														/>
													</Col>
													<Col xs={12}>
														<FormItem
															name="supportedPlayStyles"
															label={
																<div>
																	<div>
																		{t(
																			'addProductPage.form.SupportedPlayStyles',
																		)}
																	</div>
																	<div className="label-hint">
																		{t(
																			'addProductPage.form.SupportedPlayStyles.hint',
																		)}
																	</div>
																</div>
															}
															control={control}
															render={({ field }) => (
																<>
																	<Checkbox
																		onChange={() => {
																			setSinglePlayerSelected(
																				!singlePlayerSelected,
																			);
																		}}
																		checked={singlePlayerSelected}
																	>
																		{t(
																			'addProductPage.PlayStyles.SinglePlayer',
																		)}
																	</Checkbox>
																	<br />
																	<Checkbox
																		onChange={() => {
																			setOnlineCoopSelected(
																				!onlineCoopSelected,
																			);
																		}}
																		checked={onlineCoopSelected}
																	>
																		{t('addProductPage.PlayStyles.OnlineCoop')}
																	</Checkbox>
																	{onlineCoopSelected && (
																		<>
																			<FormItem
																				name="onlineCoopPlayStyleMinimum"
																				label="Minimum number of players"
																				control={control}
																				render={({ field }) => (
																					<Input
																						size="large"
																						{...field}
																						type={'number'}
																						placeholder="1"
																						min="1"
																						style={{ width: '40%' }}
																						data-testid="onlineCoopPlayStyleMinimum"
																					/>
																				)}
																				className={cx(styles.nameField)}
																			/>
																			<FormItem
																				name="onlineCoopPlayStyleMaximum"
																				label="Maximum number of players"
																				control={control}
																				render={({ field }) => (
																					<Input
																						size="large"
																						{...field}
																						type={'number'}
																						placeholder="99"
																						min="1"
																						style={{ width: '40%' }}
																					/>
																				)}
																				className={cx(styles.nameField)}
																			/>
																		</>
																	)}
																	<br />
																	<Checkbox
																		onChange={() => {
																			setOnlinePvpSelected(!onlinePvpSelected);
																		}}
																		checked={onlinePvpSelected}
																	>
																		{t('addProductPage.PlayStyles.OnlinePvP')}
																	</Checkbox>
																	{onlinePvpSelected && (
																		<>
																			<FormItem
																				name="onlinePvpPlayStyleMinimum"
																				label="Minimum number of players"
																				control={control}
																				render={({ field }) => (
																					<Input
																						size="large"
																						{...field}
																						type={'number'}
																						placeholder="1"
																						min="1"
																						style={{ width: '40%' }}
																						data-testid="onlinePvPPlayStyleMinimum"
																					/>
																				)}
																				className={cx(styles.nameField)}
																			/>
																			<FormItem
																				name="onlinePvpPlayStyleMaximum"
																				label="Maximum number of players"
																				control={control}
																				render={({ field }) => (
																					<Input
																						size="large"
																						{...field}
																						type={'number'}
																						placeholder="99"
																						min="1"
																						style={{ width: '40%' }}
																					/>
																				)}
																				className={cx(styles.nameField)}
																			/>
																		</>
																	)}
																	<br />
																	<Checkbox
																		onChange={(e) => {
																			setOnlineSocialSelected(
																				!onlineSocialSelected,
																			);
																		}}
																		checked={onlineSocialSelected}
																	>
																		{t(
																			'addProductPage.PlayStyles.OnlineSocial',
																		)}
																	</Checkbox>
																	{onlineSocialSelected && (
																		<>
																			<FormItem
																				name="onlineSocialPlayStyleMinimum"
																				label="Minimum number of players"
																				control={control}
																				render={({ field }) => (
																					<Input
																						size="large"
																						{...field}
																						type={'number'}
																						placeholder="1"
																						min="1"
																						style={{ width: '40%' }}
																						data-testid="onlineSocialPlayStyleMinimum"
																					/>
																				)}
																				className={cx(styles.nameField)}
																			/>
																			<FormItem
																				name="onlineSocialPlayStyleMaximum"
																				label="Maximum number of players"
																				control={control}
																				render={({ field }) => (
																					<Input
																						size="large"
																						{...field}
																						type={'number'}
																						placeholder="99"
																						min="1"
																						style={{ width: '40%' }}
																					/>
																				)}
																				className={cx(styles.nameField)}
																			/>
																		</>
																	)}
																</>
															)}
															className={styles.nameField}
														/>
													</Col>
												</Row>
											</div>
										</div>
									</div>
									<Button
										data-testid="submit-btn"
										loading={isLoading}
										type="primary"
										htmlType="submit"
										size="large"
									>
										{t('common.Save')}
									</Button>
								</Form>
							</FormProvider>
						)}
					</TabPane>

					<TabPane
						tab={
							<>
								{t('addProductPage.ProjectAssets')}{' '}
								{!assetsFormComplete && !(tabIndex == '2') && (
									<InfoCircleOutlined style={{ color: 'red' }} />
								)}
							</>
						}
						key="2"
					>
						{tabIndex === '2' && <AssetsForm />}
					</TabPane>
				</Tabs>
			</div>
		</>
	);
}
